// src/components/dashboard/tabs/CreateRepositoryDialog.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    Database
} from 'lucide-react';
import { repositoriesApi } from '../../../services/api';
import { useError } from '../../../contexts/ErrorContext';
import FormDialog from '../../common/FormDialog';

const CreateRepositoryDialog = ({ open, onClose, onSuccess, token }) => {
    const { t } = useTranslation();
    const { showError } = useError();

    // Состояние формы
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    // Состояния UI
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    // Обработчик изменения полей формы
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Валидация формы
    const validateForm = () => {
        if (!formData.name.trim()) {
            setFormError(t('dashboard.ai_tutors.error.repository_name_required'));
            return false;
        }
        return true;
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setFormError('');

        try {
            // Создаем новый репозиторий
            const response = await repositoriesApi.createMaterialRepository(formData, token);

            // Вызываем колбэк с новым репозиторием
            if (onSuccess) {
                onSuccess(response);
            }

            // Закрываем диалог
            handleClose();
        } catch (err) {
            setFormError(t('dashboard.ai_tutors.error.creating_repository'));
            showError(t('dashboard.ai_tutors.error.creating_repository'));
        } finally {
            setLoading(false);
        }
    };

    // Обработчик закрытия диалога
    const handleClose = () => {
        if (!loading) {
            setFormData({
                name: '',
                description: ''
            });
            setFormError('');
            onClose();
        }
    };

    // Содержимое диалога
    const dialogContent = (
        <>
            {formError && (
                <Alert severity="error" className="mb-4">
                    <AlertTitle>{t('dashboard.ai_tutors.error.title')}</AlertTitle>
                    {formError}
                </Alert>
            )}

            <div className="space-y-4 mt-2">
                {/* Название репозитория */}
                <TextField
                    name="name"
                    label={t('dashboard.ai_tutors.repository_dialog.name')}
                    value={formData.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                />

                {/* Описание репозитория */}
                <TextField
                    name="description"
                    label={t('dashboard.ai_tutors.repository_dialog.description')}
                    value={formData.description}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    fullWidth
                />

                <p className="text-sm text-gray-500 mt-2">
                    {t('dashboard.ai_tutors.repository_dialog.note')}
                </p>
            </div>
        </>
    );

    // Иконка для заголовка
    const titleIcon = <Database className="text-blue-500 mr-2" />;

    return (
        <FormDialog
            open={open}
            onClose={handleClose}
            title={
                <div className="flex items-center">
                    {titleIcon}
                    {t('dashboard.ai_tutors.repository_dialog.title')}
                </div>
            }
            submitLabel={loading ? t('dashboard.ai_tutors.repository_dialog.creating') : t('common.create')}
            cancelLabel={t('common.cancel')}
            onSubmit={handleSubmit}
            isSubmitting={loading}
            isValid={formData.name.trim() !== ''}
            maxWidth="md"
        >
            {dialogContent}
        </FormDialog>
    );
};

export default CreateRepositoryDialog;
